import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import { PaginationDefaults } from '@terra/shared-dto-types';
import { terraProxyApiClient } from '../../clients/testerClient';

export const MENTIONS_SIGNALS_QUERY_KEY = 'mentions-signals';

const MENTIONS_SIGNALS_PAGE_SIZE = PaginationDefaults.LIMIT;

type MentionsSignalsPage = {
  signals: GroupedSignal[];
  totalCount: number;
};

export function useMentionsSignals({
  targetId,
  targetType,
  searchTerm,
  enabled = true,
}: {
  targetId: string | null | undefined;
  targetType: TargetTypeType;
  searchTerm: string;
  enabled?: boolean;
}) {
  const trimmedSearch = searchTerm.trim();

  const query = useInfiniteQuery({
    queryKey: [MENTIONS_SIGNALS_QUERY_KEY, targetId, targetType, trimmedSearch],
    queryFn: async ({ pageParam = 0 }): Promise<MentionsSignalsPage> => {
      if (!targetId) {
        return { signals: [], totalCount: 0 };
      }

      const skip = pageParam * MENTIONS_SIGNALS_PAGE_SIZE;
      const filters =
        trimmedSearch.length > 0 ? { description: trimmedSearch } : {};

      const result = await terraProxyApiClient.listSignalsByTarget({
        targetId,
        targetType,
        skip,
        limit: MENTIONS_SIGNALS_PAGE_SIZE,
        filters,
      });

      return {
        signals: result.signals,
        totalCount: result.totalCount,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce(
        (count, page) => count + page.signals.length,
        0,
      );

      return loadedCount < lastPage.totalCount ? allPages.length : undefined;
    },
    enabled: Boolean(targetId) && enabled,
    staleTime: 30_000,
  });

  const signals = useMemo(
    () => query.data?.pages.flatMap((page) => page.signals) ?? [],
    [query.data],
  );

  return {
    signals,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
  };
}
